import { safeGetItem, safeSetItem, safeRemoveItem } from "@/lib/safeStorage";

/**
 * Autoaccept do Dr. Felipe Ferraz (aceite automático de 20s).
 *
 * Quando a fila do plantão é aceita automaticamente (queueAutoAssignJob →
 * queue_requests.auto_assigned = true), o paciente vê a tela "Consulta aceita —
 * continue pelo WhatsApp" em vez do fluxo normal de videochamada.
 *
 * ⚠️ O autoaccept SÓ funciona para o Dr. Felipe Ferraz (pedido do dono): o backend
 * atribui o aceite automático apenas pra ele, e o frontend só exibe a tela do
 * WhatsApp quando o médico da fila é ele (AUTOACCEPT_DOCTOR_ID). Qualquer outro
 * médico segue o fluxo normal de vídeo.
 *
 * O marcador `autoaccept_whatsapp` no localStorage faz com que, se o paciente
 * fechar e voltar ao portal, ele continue vendo essa mesma tela.
 *
 * ⚠️ Esse comportamento SÓ deve valer enquanto a consulta NÃO foi finalizada.
 * Quando o médico finaliza o atendimento (appointment.ended_at preenchido ou
 * status completed/cancelled), o marcador precisa ser limpo — senão o paciente
 * fica preso na tela do WhatsApp para sempre e nunca consegue gerar outra
 * consulta.
 *
 * ⚠️ IDENTIFICAÇÃO PRECISA (não usar doctor + type): o admin também cria
 * consultas do Dr. Felipe Ferraz com type='emergency' (início imediato sem
 * pagamento via /admin). Essas NÃO passaram pelo autoaccept e devem seguir o
 * fluxo normal de videochamada — sem botão de WhatsApp. Por isso a
 * identificação de autoaccept usa o marcador volátil `autoaccept_whatsapp`
 * (gravado SÓ no fluxo real, no /sala-espera quando auto_assigned=true) e o id
 * persistente `autoaccept_appointment_id` (que sobrevive ao botão
 * "Já realizei a consulta", mantendo o card do portal com "Abrir WhatsApp"
 * enquanto a consulta auto-aceita estiver ativa).
 */

/** Dr. Felipe Ferraz — único médico com autoaccept habilitado (id na tabela doctors). */
export const AUTOACCEPT_DOCTOR_ID = "5a9f64c4-24d4-4fa8-9709-9dca3e3ba7e5";
/** Nome do dono do autoaccept (usado como fallback de exibição/tela). */
export const AUTOACCEPT_DOCTOR_NAME = "Dr. Felipe Ferraz";
/** WhatsApp do dono do autoaccept (fallback da tela "Consulta aceita"). */
export const AUTOACCEPT_DOCTOR_PHONE = "54992371000";

/**
 * Chave persistente com o id da consulta que passou pelo autoaccept.
 *
 * Diferente do marcador volátil `autoaccept_whatsapp` (limpo quando o paciente
 * clica "Já realizei a consulta" ou inicia uma consulta nova), este id
 * permanece enquanto a consulta auto-aceita estiver ativa — é ele que garante
 * que o card do portal continue mostrando "Abrir WhatsApp" mesmo depois do
 * marcador volátil sumir (bug real 26/08/2026).
 *
 * ⚠️ SÓ é gravado no fluxo REAL do autoaccept (/sala-espera ao detectar
 * auto_assigned=true no Dr. Felipe Ferraz). Consultas criadas pelo /admin
 * (mesmo do Dr. Felipe, type='emergency') nunca passam por aqui — assim o
 * portal não mostra o botão de WhatsApp para elas.
 */
const AUTOACCEPT_APPOINTMENT_ID_KEY = "autoaccept_appointment_id";

/** Grava o id da consulta auto-aceita (persistente — sobrevive ao forceClear). */
export function setAutoAcceptAppointmentId(id: number | string | null | undefined): void {
  try {
    if (id == null) {
      safeRemoveItem(AUTOACCEPT_APPOINTMENT_ID_KEY);
    } else {
      safeSetItem(AUTOACCEPT_APPOINTMENT_ID_KEY, String(id));
    }
  } catch {
    /* ignora */
  }
}

/** Devolve o id da consulta auto-aceita (ou null se não houver). */
export function getAutoAcceptAppointmentId(): string | null {
  try {
    return safeGetItem(AUTOACCEPT_APPOINTMENT_ID_KEY);
  } catch {
    return null;
  }
}

export interface AutoAcceptAppointmentLike {
  id: number;
  status?: string | null;
  started_at?: string | null;
  ended_at?: string | null;
  type?: string | null;
}

/** True se a consulta ainda está em andamento (iniciada e não finalizada). */
export function isAppointmentStillActive(
  appt: AutoAcceptAppointmentLike | null | undefined
): boolean {
  if (!appt) return false;
  if (appt.ended_at) return false;
  const st = (appt.status || "").toLowerCase();
  if (st === "completed" || st === "cancelled" || st === "canceled") return false;
  return !!appt.started_at;
}

/**
 * Remove o marcador do autoaccept e, se o `active_emergency` apontar pra mesma
 * fila, limpa também (senão o restoreSession redirecionaria de volta pra sala
 * de espera mesmo depois da consulta ter acabado).
 */
function clearAutoAcceptState(auto: { queueId?: number | null } | null): void {
  try {
    safeRemoveItem("autoaccept_whatsapp");
    if (auto?.queueId != null) {
      const raw = safeGetItem("active_emergency");
      if (raw) {
        const em = JSON.parse(raw);
        if (em && String(em.emergencyId) === String(auto.queueId)) {
          safeRemoveItem("active_emergency");
        }
      }
    }
  } catch {
    /* ignora */
  }
}

/**
 * Verifica se o autoaccept ainda é válido para o paciente comparando com a
 * lista de consultas retornada por `patientAPI.dashboard()`.
 *
 * - Consulta ainda em andamento → mantém o marcador e devolve `true`.
 * - Consulta já finalizada (ou marcador antigo/inválido) → limpa o marcador
 *   (e o active_emergency da mesma fila) e devolve `false`.
 *
 * @param patientId     id do patient_case logado (patientCaseId salvo no marcador)
 * @param appointments  lista completa de consultas do paciente (dashboard)
 */
export function isAutoAcceptStillActive(
  patientId: number | string,
  appointments: AutoAcceptAppointmentLike[] | null | undefined
): boolean {
  try {
    const raw = safeGetItem("autoaccept_whatsapp");
    if (!raw) return false;

    const auto = JSON.parse(raw);
    if (!auto || !auto.queueId || String(auto.patientCaseId) !== String(patientId)) {
      clearAutoAcceptState(auto);
      return false;
    }

    // O autoaccept é exclusivo do Dr. Felipe Ferraz: se o marcador registrou o
    // doctorId e ele NÃO é o do Dr. Felipe (marcador antigo/estranho), não vale.
    if (auto.doctorId && String(auto.doctorId) !== AUTOACCEPT_DOCTOR_ID) {
      clearAutoAcceptState(auto);
      return false;
    }

    const appts = appointments || [];

    // Se conhecemos o appointment exato, valida só ele.
    if (auto.appointmentId != null) {
      const appt = appts.find((a) => String(a.id) === String(auto.appointmentId));
      if (appt) {
        const active = isAppointmentStillActive(appt);
        if (!active) clearAutoAcceptState(auto);
        return active;
      }
      // Não achou o appointment na lista → sem como confirmar que está ativo.
      clearAutoAcceptState(auto);
      return false;
    }

    // Fallback (sem appointmentId): mantém apenas se houver consulta em andamento.
    const active = appts.some(isAppointmentStillActive);
    if (!active) clearAutoAcceptState(auto);
    return active;
  } catch {
    // Marcador corrompido ou erro inesperado: nunca prender o paciente na tela.
    clearAutoAcceptState(null);
    return false;
  }
}

/**
 * Limpa o marcador do autoaccept e o id persistente da consulta auto-aceita
 * (usado ao iniciar uma nova consulta / quando a consulta auto-aceita finaliza).
 */
export function clearAutoAccept(): void {
  try {
    safeRemoveItem("autoaccept_whatsapp");
    safeRemoveItem(AUTOACCEPT_APPOINTMENT_ID_KEY);
  } catch {
    /* ignora */
  }
}

/**
 * Limpa FORÇADAMENTE o marcador do autoaccept e o `active_emergency` da mesma
 * fila. Usado quando o paciente sai da tela do WhatsApp por vontade própria
 * (botão "Voltar ao portal" / após o tempo de liberação), pra ele NÃO ser
 * redirecionado de volta pra tela do WhatsApp ao entrar no portal.
 *
 * ⚠️ NÃO limpa o id persistente `autoaccept_appointment_id`: a consulta
 * auto-aceita continua ativa no backend até o médico finalizar, e o card do
 * portal deve continuar mostrando "Abrir WhatsApp" (nunca videochamada).
 */
export function forceClearAutoAcceptAndEmergency(): void {
  try {
    const raw = safeGetItem("autoaccept_whatsapp");
    let queueId: number | null = null;
    if (raw) {
      try {
        const auto = JSON.parse(raw);
        if (auto?.queueId != null) queueId = Number(auto.queueId);
      } catch {
        /* ignora */
      }
    }
    safeRemoveItem("autoaccept_whatsapp");
    if (queueId != null) {
      const emRaw = safeGetItem("active_emergency");
      if (emRaw) {
        try {
          const em = JSON.parse(emRaw);
          if (em && String(em.emergencyId) === String(queueId)) {
            safeRemoveItem("active_emergency");
          }
        } catch {
          /* ignora */
        }
      }
    }
  } catch {
    /* ignora */
  }
}

/**
 * "28/09/2026 às 14:30" a partir do `appointment_date`/`appointment_time`.
 *
 * Monta a data a partir dos PEDAÇOS da string (ano/mês/dia), nunca com
 * `new Date("2026-09-28")` — esse construtor interpreta a data como UTC e, em
 * Brasília (-03:00), devolve o DIA ANTERIOR. É o padrão de bug de data já
 * registrado várias vezes neste projeto. Aqui também não se usa `.split("-")`
 * cru sem validar: a coluna pode vir serializada como ISO completo
 * ("2026-09-28T03:00:00.000Z") em vez de "YYYY-MM-DD".
 */
function formatScheduleLabel(date: string, time: string): string {
  const d = String(date).slice(0, 10); // corta o "T..." se vier ISO completo
  const [y, m, dd] = d.split("-");
  const hhmm = String(time).slice(0, 5); // "14:30:00" -> "14:30"
  if (!y || !m || !dd) return hhmm ? `${d} às ${hhmm}` : d;
  return `${dd}/${m}/${y} às ${hhmm}`;
}

/** Número wa.me internacional: "55" + dígitos (mantém o 55 se já vier nele). */
function buildWaNumber(phone: string): string {
  const digits = (phone || "").replace(/\D/g, "");
  if (digits.length === 0) return "";
  return digits.startsWith("55") ? digits : `55${digits}`;
}

/**
 * Constrói o link wa.me do autoaccept (consulta aceita automaticamente) com a
 * mensagem padrão preenchida com os dados reais daquela consulta. Usado tanto
 * na tela "Consulta aceita" quanto no botão "Chamar no WhatsApp" do dashboard
 * ("Minhas consultas") — garante que o paciente SEMPRE chame o médico direto
 * pelo WhatsApp, sem voltar ao lobby nem à videochamada.
 */
export function buildAutoAcceptWhatsAppUrl(opts: {
  patientName: string;
  patientAge?: string;
  prontuarioNumber: string | number;
  doctorPhone?: string;
  doctorName?: string;
  /**
   * Consulta AGENDADA (pedido do dono, 28/09/2026): quando a consulta tem
   * data/hora marcada, a mensagem diz que é agendada e informa o horário —
   * sem isso o Dr. Felipe recebia o mesmo texto do plantão e não sabia se
   * era para atender na hora ou se a pessoa tinha hora marcada.
   * Ambos os campos são opcionais: sem eles o texto continua como antes.
   */
  scheduledDate?: string;
  scheduledTime?: string;
  /** Consulta de DEPENDENTE: quem escreve é o responsável (patientName) e a consulta é para este nome. */
  dependentName?: string;
}): string {
  const displayName = opts.doctorName?.trim()
    ? /^dr\.?\s/i.test(opts.doctorName.trim())
      ? opts.doctorName.trim()
      : `Dr. ${opts.doctorName.trim()}`
    : AUTOACCEPT_DOCTOR_NAME;

  const lines = [
    "Blink Telemedicina",
    "━━━━━━━━━━━━━",
    `Olá ${displayName}!`,
    "",
    `Meu nome é ${opts.patientName}`,
  ];
  const dependent = opts.dependentName?.trim();
  if (dependent) {
    lines.push(`A consulta é para meu dependente *${dependent}*.`);
    if (opts.patientAge) lines.push(`Idade do dependente: ${opts.patientAge} anos.`);
  } else if (opts.patientAge) {
    // Só inclui a linha de idade quando existe (evita "Tenho  anos." em dados incompletos)
    lines.push(`Tenho ${opts.patientAge} anos.`);
  }
  const agendada = Boolean(opts.scheduledDate && opts.scheduledTime);
  lines.push("");
  if (agendada) {
    lines.push(
      "Tenho uma *consulta agendada*.",
      `📅 ${formatScheduleLabel(opts.scheduledDate!, opts.scheduledTime!)}`,
    );
  } else {
    lines.push("Desejo realizar minha consulta médica.");
  }
  lines.push(
    "━━━━━━━━━━━━━",
    `Prontuário #${opts.prontuarioNumber}`,
  );
  const message = lines.join("\n");
  const waNumber = buildWaNumber(opts.doctorPhone || AUTOACCEPT_DOCTOR_PHONE);
  return `https://wa.me/${waNumber}?text=${encodeURIComponent(message)}`;
}
